import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../domain/api';

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const formatCurrency = (amount, currency = 'INR') => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return null;
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return numericAmount.toFixed(2);
  }
};

const normalizeMoney = (value, currency = 'INR') => {
  if (typeof value === 'string' && /[A-Za-z]/.test(value) === false && value.includes('.')) {
    return formatCurrency(Number(value), currency);
  }

  if (typeof value === 'number') {
    return formatCurrency(value, currency);
  }

  if (typeof value === 'string' && (value.includes('₹') || value.includes('$'))) {
    return value;
  }

  return null;
};

export const normalizeFacebookAdAccount = (account = {}) => {
  const currency = firstDefined(account.currency, account.currencyCode, 'INR');
  const accountId = firstDefined(account.accountId, account.account_id, account.id);
  const accountName = firstDefined(account.accountName, account.name, 'Unnamed account');
  const businessName = firstDefined(
    account.businessName,
    account.raw?.business_name,
    account.business_name,
    account.end_advertiser_name,
    account.business?.name,
    null,
  );
  const status = firstDefined(account.status, account.accountStatus, null);
  const paymentType = firstDefined(account.paymentType, account.payment_type, 'Unknown');
  const timezoneName = firstDefined(
    account.timezoneName,
    account.raw?.timezone_name,
    account.timezone_name,
    null,
  );
  const ownerName = firstDefined(
    account.ownerName,
    account.owner?.name,
    typeof account.owner === 'string' ? account.owner : null,
    account.raw?.owner?.name,
    account.raw?.business?.name,
    account.business?.name,
    account.owner?.id,
    null,
  );
  const fundingSourceDisplay = firstDefined(
    account.fundingSourceDisplay,
    account.raw?.funding_source_details?.display_string,
    null,
  );

  const amountSpentLabel = firstDefined(
    account.amountSpentLabel,
    normalizeMoney(account.amountSpent, currency),
    normalizeMoney(account.raw?.amount_spent, currency),
    normalizeMoney(account.amount_spent, currency),
    null,
  );
  const spendCapLabel = firstDefined(
    account.spendCapLabel,
    normalizeMoney(account.spendCap, currency),
    normalizeMoney(account.raw?.spend_cap, currency),
    normalizeMoney(account.spend_cap, currency),
    account.spendCap === null ? 'Not Set' : null,
    null,
  );
  const availableBalanceLabel = firstDefined(
    account.availableBalanceLabel,
    normalizeMoney(account.availableBalance, currency),
    normalizeMoney(account.raw?.balance, currency),
    normalizeMoney(account.available_balance, currency),
    null,
  );
  const availableFundsLabel = firstDefined(
    account.availableFundsLabel,
    normalizeMoney(account.availableFunds, currency),
    fundingSourceDisplay,
    normalizeMoney(account.raw?.prepay_balance, currency),
    null,
  );
  const currentBalanceLabel = firstDefined(
    account.currentBalanceLabel,
    normalizeMoney(account.currentBalance, currency),
    normalizeMoney(account.raw?.balance, currency),
    null,
  );

  return {
    ...account,
    accountId,
    accountName,
    businessName,
    status,
    paymentType,
    timezoneName,
    ownerName,
    fundingSourceDisplay,
    currency,
    amountSpentLabel,
    spendCapLabel:
      spendCapLabel || (account.spendCap === 0 || account.spend_cap === '0' ? 'Not Set' : null),
    availableFundsLabel,
    currentBalanceLabel,
    availableBalanceLabel,
  };
};

// Hook to get all Facebook accounts for the dropdown (React Query v4)
export const useSelectAllFacebookAccount = () => {
  const query = useQuery({
    queryKey: ['facebook-accounts', 'api-v3'],
    queryFn: async () => {
      const response = await api.get('/facebook-ads/ad-accounts');
      const adAccounts = (response.data?.adAccounts || []).map(
        normalizeFacebookAdAccount,
      );

      return {
        ...response.data,
        adAccounts,
        // Legacy compatibility for upload screens that still expect this shape.
        facebook_accounts:
          response.data?.facebook_accounts ||
          adAccounts.map((account) => ({
            _id: account.accountId,
            name: account.accountName,
          })),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  return [query.data || { adAccounts: [] }, query.isLoading, query.refetch];
};

// Hook to fetch a single Facebook ads report (React Query v4)
export const useSingleFacebookAdsReport = (id) => {
  const query = useQuery({
    queryKey: ['facebook-ads', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get(`/facebook-ads/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
  return [
    {
      report_loading: query.isLoading,
      report: query.data,
      edit_report_loading: false,
    },
  ];
};

// Hook to update a Facebook ads report (React Query v4)
export const useUpdateFacebookAdsReport = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/facebook-ads/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['facebook-ads']);
    },
  });
  const updateData = (id, data) => mutation.mutateAsync({ id, data });
  return [updateData, { edit_report_loading: mutation.isLoading }];
};

// Hook to delete a Facebook ads report (React Query v4)
export const useDeleteFacebookAdsReport = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/facebook-ads/${id}`);
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['facebook-ads']);
    },
  });
  const deleteReport = (id) => mutation.mutateAsync(id);
  return [deleteReport];
};
